import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { formatEther, parseEther } from 'viem'
import { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { useCurrencyFormatter } from '@/hooks/helpers/useCurrencyFormatter'
import { zodResolver } from '@hookform/resolvers/zod'
import { useWriteStaking2aContract } from '@/hooks/staking2a-contract/useWriteStaking2aContract'
import { usePepperContractAddress } from '@/hooks/pepper-contract/usePepperContract'
import { waitForTransactionReceipt } from '@/utils/wagmi/waitForTransactionReceipt'
import { AmountForm } from './AmountForm'
import { logger } from '@/observability/logger'
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	type ResponsiveDialogProps
} from '@/components/ui/ResponsiveDialog'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import { useTracking } from '@/providers/TrackingProvider'

type TokenDisplay = {
	label: string
	value: bigint
}

type UnstakesDialogProps = Omit<ResponsiveDialogProps, 'children'> & {
	onSubmit: () => void
	closeDialog: () => void
	lockedTokens: bigint
	totalStaked: bigint
	unlockedTokens: bigint
	totalClaimable: bigint
}

const unstakingFormSchema = (unlockedTokens: bigint, isUnstaked: boolean) =>
	z.object({
		amount: isUnstaked
			? z.string()
			: z
					.string()
					.regex(/^\d*\.?\d*$/, 'Invalid number')
					.refine(x => Number(x) >= 1, 'Must be at least 1 PEPPER')
					.refine(
						x => Number(x) <= Number(formatEther(unlockedTokens)),
						'Amount exceeds your available balance'
					)
	})

export function UnstakeDialog({
	onSubmit,
	closeDialog,
	lockedTokens,
	totalStaked,
	unlockedTokens,
	totalClaimable,
	...dialogProps
}: UnstakesDialogProps) {
	// Contract Hooks
	const format = useCurrencyFormatter(3) // 3 decimal places
	const pepperAddress = usePepperContractAddress()
	const writeStaking2aContract = useWriteStaking2aContract()

	const [isUnstaked, setIsStaked] = useState(false)

	const { sendEvent } = useTracking()

	// Form State
	const form = useForm({
		resolver: zodResolver(unstakingFormSchema(unlockedTokens, isUnstaked))
	})
	const amount = useWatch({ control: form.control, name: 'amount' })

	// Set default amount to unlocked tokens
	useEffect(() => {
		const defaultValue = formatEther(unlockedTokens) ?? '0'
		form.setValue('amount', defaultValue, {
			shouldValidate: true
		})
	}, [unlockedTokens])

	const tokenDisplays: TokenDisplay[] = [
		{ label: 'PEPPER Staked', value: totalStaked },
		{ label: 'PEPPER Locked', value: lockedTokens },
		{ label: 'PEPPER Claimable', value: totalClaimable },
		{ label: 'Available to Unstake', value: unlockedTokens }
	]

	const handleUnstake = form.handleSubmit(async () => {
		try {
			sendEvent('click_unstake_pepper')
			const amountInWei = parseEther(amount)
			const hash = await writeStaking2aContract({
				functionName: 'unstake',
				args: [amountInWei, pepperAddress]
			})

			await waitForTransactionReceipt({ hash })
			sendEvent('unstake_pepper_successful', { amount, txHash: hash })
			setIsStaked(true)
			onSubmit()
		} catch (error) {
			logger.error(error, 'Error unstaking Pepper')
			toast.error('An error occurred while unstaking Pepper.')
		}
	})

	const handleClaim = form.handleSubmit(async () => {
		try {
			sendEvent('click_claim_pepper')
			const hash = await writeStaking2aContract({
				functionName: 'claim',
				args: [pepperAddress]
			})

			await waitForTransactionReceipt({ hash })
			sendEvent('claim_pepper_successful', { txHash: hash })
			onSubmit()
			closeDialog()
		} catch (error) {
			logger.error(error, 'Error claiming Pepper')
			toast.error('An error occurred while claiming Pepper.')
		}
	})

	const submitButtonLabel = isUnstaked ? 'claim' : 'unstake'

	return (
		<ResponsiveDialog {...dialogProps}>
			<ResponsiveDialogContent>
				<div className="overflow-y-auto flex flex-col gap-8">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>How many Pepper do you want to unstake?</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>
					<div>
						<AmountForm form={form} base={unlockedTokens} disabled={isUnstaked} key="unstake" />
					</div>
					<div className="gap-2">
						{tokenDisplays.map(({ label, value }) => (
							<h4 key={label} className="text-center text-3xl text-green-50 leading-none m-2">
								{label}: {format(formatEther(value))}
							</h4>
						))}
					</div>
					<ResponsiveDialogFooter>
						<Button
							onClick={isUnstaked ? handleClaim : handleUnstake}
							variant="danger"
							disabled={
								isUnstaked ? false : !form.formState.isValid || !amount || form.formState.isSubmitting
							}
							loading={form.formState.isSubmitting}
							className="py-5 px-10 leading-none capitalize"
							data-testId={testIdFactory.pepperAmountFormButton(submitButtonLabel)}
						>
							{submitButtonLabel}
						</Button>
					</ResponsiveDialogFooter>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	)
}
